import { createContext, useContext, useState, useEffect } from 'react';
import {
  getCompactApplications,
  updateCompactApplication as updateCompactApplicationRequest,
} from '../../api/endpoints/application';
import { StatePositionContext } from './PositionContext';

const createEmptyCompactApplications = () => ({
  applied: [],
  shortlisted: [],
  interview: [],
  personal_rejection: [],
  hired: [],
  to_be_rejected: [],
  rejected: [],
});

const getCompactApplicationCategory = (application) => {
  const { status, substatus } = application || {};

  if (status === 'rejected' || substatus === 'rejected') return 'rejected';
  if (['applied', 'shortlisted', 'interview', 'personal_rejection', 'hired', 'to_be_rejected'].includes(substatus)) return substatus;
  if (status === 'invited') return 'shortlisted';

  return 'applied';
};

const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

export const sortCompactApplications = (applications = []) => {
  const grouped = applications.reduce((acc, application) => {
    const category = getCompactApplicationCategory(application);
    acc[category].push(application);
    return acc;
  }, createEmptyCompactApplications());

  for (const column of Object.keys(grouped)) {
    if (column === 'shortlisted') {
      const invited = grouped[column].filter((app) => app.status === 'invited').sort(byNewest);
      const rest = grouped[column].filter((app) => app.status !== 'invited').sort(byNewest);
      grouped[column] = [...invited, ...rest];
    } else {
      grouped[column] = [...grouped[column]].sort(byNewest);
    }
  }

  return grouped;
};

export const StateKanbanContext = createContext();
export const DispatchKanbanContext = createContext();

export const KanbanProvider = ({ children }) => {
  const { single } = useContext(StatePositionContext);
  const positionId = single?.id;
  const [compactApplications, setCompactApplications] = useState(createEmptyCompactApplications());
  const [loadingCompactApplications, setLoadingCompactApplications] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const toggleColumnSelection = (columnIds) => {
    setSelectedIds((prev) => {
      const allSelected = columnIds.every((id) => prev.has(id));
      const next = new Set(prev);

      if (allSelected) {
        columnIds.forEach((id) => next.delete(id));
      } else {
        columnIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const isSelected = (id) => selectedIds.has(id);
  const isAllSelected = (columnIds) => columnIds.length > 0 && columnIds.every((id) => selectedIds.has(id));
  const isIndeterminate = (columnIds) => columnIds.some((id) => selectedIds.has(id)) && !columnIds.every((id) => selectedIds.has(id));

  const deselect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const fetchCompactApplicationsData = async () => {
    setCompactApplications(createEmptyCompactApplications());
    if (!positionId) {
      setLoadingCompactApplications(false);
      return;
    }
    setLoadingCompactApplications(true);
    try {
      const response = await getCompactApplications({
        params: {
          positionId,
          // delay: 1500
        },
      });

      setCompactApplications(sortCompactApplications(response?.data));
    } catch (error) {
      setCompactApplications(createEmptyCompactApplications());
    } finally {
      setLoadingCompactApplications(false);
    }
  };

  useEffect(() => {
    setSelectedIds(new Set());
    setCompactApplications(createEmptyCompactApplications());
    fetchCompactApplicationsData();
  }, [positionId]);

  const applyCompactApplicationUpdate = ({ applicationId, stars, substatus }) => {
    setCompactApplications((prev) => {
      const flat = Object.values(prev).flat();
      const updated = flat.map((app) =>
        app.id === applicationId
          ? {
            ...app,
            ...(substatus !== undefined && { substatus }),
            ...(stars !== undefined && { stars }),
          }
          : app,
      );

      return sortCompactApplications(updated);
    });
  };

  const updateCompactApplication = async ({
    applicationId,
    stars,
    substatus,
    persist = true,
  }) => {
    const previousState = compactApplications;

    applyCompactApplicationUpdate({ applicationId, stars, substatus });

    if (!persist) {
      return { success: true };
    }

    const response = await updateCompactApplicationRequest({
      selector: applicationId,
      body: {
        ...(substatus !== undefined && { substatus }),
        ...(stars !== undefined && { stars }),
      },
    });

    if (!response?.success) {
      setCompactApplications(previousState);
    }

    return response;
  };

  return (
    <DispatchKanbanContext.Provider
      value={{
        setCompactApplications,
        refetchCompactApplications: fetchCompactApplicationsData,
        updateCompactApplication,
        toggleSelection,
        toggleColumnSelection,
        deselect,
        clearSelection,
      }}
    >
      <StateKanbanContext.Provider
        value={{
          compactApplications,
          loadingCompactApplications,
          selectedIds,
          isSelected,
          isAllSelected,
          isIndeterminate,
        }}
      >
        {children}
      </StateKanbanContext.Provider>
    </DispatchKanbanContext.Provider>
  );
};

export const useKanbanState = () => useContext(StateKanbanContext);
export const useKanbanDispatch = () => useContext(DispatchKanbanContext);
